import { useTranslation } from 'react-i18next';
import { DataDisplay, DataStack, HeaderStack, TitleStack } from '../styles';
import Card from '@/components/Card';
import Typography from '@mui/material/Typography';
import { FilterIcon, FilterX, XIcon } from 'lucide-react';
import IconButton from '@mui/material/IconButton';
import TextField from '@/components/Input/TextField';
import NumberField from '@/components/Input/NumberField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Button } from '@/components/Button';
import { useFormik } from 'formik';

interface MapFilterType {
  splitter: string | null;
  online: string | null;
  channel: number | null;
}

export type MapFilterQuery = {
  channel?: string;
  splitterDescription?: string;
  online?: boolean;
} | null;

// formik blank initialvalue
const emptyFilterValues: MapFilterType = {
  splitter: '',
  online: null,
  channel: null,
};

// translate the API query param shape, to initial form values shape
// if selectedFilter is null, return empty values
function getInitialValues(selectedFilter: MapFilterQuery): MapFilterType {
  return {
    splitter:
      typeof selectedFilter?.splitterDescription === 'string'
        ? selectedFilter.splitterDescription
        : '',
    online:
      typeof selectedFilter?.online === 'boolean'
        ? selectedFilter.online
          ? 'online'
          : 'offline'
        : null,
    channel:
      selectedFilter?.channel !== undefined && selectedFilter.channel !== ''
        ? Number(selectedFilter.channel)
        : null,
  };
}

function FilterCard({
  filterOpen,
  setFilterOpen,
  selectedFilter,
  setSelectedFilter,
}: {
  filterOpen: boolean;
  setFilterOpen: (open: boolean) => void;
  selectedFilter: MapFilterQuery;
  setSelectedFilter: (filter: MapFilterQuery) => void;
}) {
  const { t } = useTranslation();

  const formik = useFormik<MapFilterType>({
    initialValues: getInitialValues(selectedFilter),
    enableReinitialize: true,
    onSubmit: (values) => {
      // mount query object with form fields values
      const filterObject: NonNullable<MapFilterQuery> = {};

      if (values.splitter) {
        filterObject.splitterDescription = values.splitter;
      }
      if (values.online) {
        filterObject.online = values.online === 'online';
      }
      if (values.channel !== null && values.channel !== undefined) {
        filterObject.channel = String(values.channel);
      }

      setSelectedFilter(
        Object.keys(filterObject).length > 0 ? filterObject : null
      );
    },
  });

  return (
    <DataDisplay $open={filterOpen}>
      <Card sx={{ height: '100%', padding: 2 }}>
        {/** TITLE */}
        <HeaderStack>
          <TitleStack>
            <FilterIcon size={20} />
            <Typography variant="h5">{t('map.filter.title')}</Typography>
          </TitleStack>
          <IconButton onClick={() => setFilterOpen(false)}>
            <XIcon />
          </IconButton>
        </HeaderStack>

        <DataStack sx={{ my: 2 }}>
          {/** FILTER FIELDS */}
          {/** Splitter */}
          <TextField
            placeholder={t('map.filter.splitterPlaceholder')}
            label="Splitter"
            name="splitter"
            onChange={formik.handleChange}
            value={formik.values.splitter ?? ''}
          />

          {/** Online/Offline */}
          <ToggleButtonGroup
            color="primary"
            value={formik.values.online}
            onChange={(_, newValue) => {
              if (newValue !== null) {
                formik.setFieldValue('online', newValue);
              }
            }}
            sx={{ width: '100%' }}
            size="small"
            exclusive
          >
            <ToggleButton value="online" sx={{ width: '100%' }}>
              {t('map.filter.online')}
            </ToggleButton>
            <ToggleButton value="offline" sx={{ width: '100%' }}>
              {t('map.filter.offline')}
            </ToggleButton>
          </ToggleButtonGroup>

          {/** Channel */}
          <NumberField
            label="Channel"
            min={1}
            max={177}
            value={formik.values.channel}
            size="small"
            onValueChange={(value) => formik.setFieldValue('channel', value)}
          />

          {/** Apply and Clear Buttons */}
          <HeaderStack>
            <Button
              startIcon={<FilterX size={18} />}
              variant="outlined"
              onClick={() => {
                formik.resetForm({ values: emptyFilterValues });
                setSelectedFilter(null);
              }}
            >
              {t('map.filter.clearFilters')}
            </Button>
            <Button
              endIcon={<FilterIcon size={18} />}
              variant="contained"
              onClick={() => formik.handleSubmit()}
            >
              {t('map.filter.applyFilters')}
            </Button>
          </HeaderStack>
        </DataStack>
      </Card>
    </DataDisplay>
  );
}

export default FilterCard;
